/**
 * PURCHASED vs USED, PLAINLY. OA-0001 §3.11, 08 §7, 09 §5.1's "313 is not 313".
 *
 * One component, used by the Control Plane's view of a customer's capacity and by the office's view
 * of its own, so the two can never disagree about what "18 companies" means. Every line states all
 * three numbers — purchased, used, remaining — because a bare usage figure is the number a customer
 * cannot reconcile against an invoice.
 *
 * NOTHING HERE DECIDES ANYTHING. `capacityText.ts` subtracts and writes sentences; the decision
 * "may another one be created" is `checkCapacity` on the server and arrives, when it matters, as a
 * `CapacityRefusal` rendered by `<CapacityRefusalNotice>` below.
 *
 * The over-capacity line is not an error state. A plan change can lower a limit under existing
 * usage, every existing row keeps working, and the sentence says both halves — because saying only
 * the first is what makes an office expect a deletion that is never coming.
 */

import type { CapacityRefusal, CapacityUsage } from '../api/contract';
import { capacityLineText, capacityRefusalText } from '../api/capacityText';
import { StatusChip } from './StatusChip';

export function CapacityPanel({
  capacity,
  captionHe,
}: {
  readonly capacity: readonly CapacityUsage[];
  readonly captionHe: string;
}): JSX.Element {
  if (capacity.length === 0) {
    // T12's rule one level down: zero rows is a written state, never an empty box.
    return (
      <section className="state-block state-block--empty">
        <h2 className="state-block__title">{captionHe}</h2>
        <p className="state-block__body">לא נרשמו מכסות למנוי הזה.</p>
      </section>
    );
  }

  return (
    <section className="capacity">
      <h2 className="capacity__caption">{captionHe}</h2>
      <div className="capacity__lines">
        {capacity.map((usage) => {
          const line = capacityLineText(usage);
          return (
            <article key={usage.kind} className="capacity__line" data-tone={line.tone}>
              <h3 className="capacity__title">{line.titleHe}</h3>
              {/* The digits run LTR inside an RTL sentence, under an explicit dir (09 §9.5 A1). */}
              <p className="capacity__summary" dir="rtl">
                <span dir="ltr" className="capacity__numbers">
                  {line.summaryHe}
                </span>
              </p>
              {line.overCapacityHe === null ? null : (
                <p className="capacity__over">{line.overCapacityHe}</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * THE REFUSAL. Never a generic error — 08 §7 requires the count, the limit and a route, and this
 * renders exactly those three plus the one sentence that keeps a customer from expecting a deletion.
 *
 * O-3: no price and no purchase button. The person who reaches a limit is frequently not the person
 * who may spend the money, so the next step is a place to go, not a transaction.
 */
export function CapacityRefusalNotice({ refusal }: { readonly refusal: CapacityRefusal }): JSX.Element {
  const text = capacityRefusalText(refusal);
  return (
    <section className="denial" data-tone="blocked" role="alert">
      <h2 className="denial__title">{text.titleHe}</h2>
      <p className="denial__explanation">{text.explanationHe}</p>
      <p className="capacity__counts">
        <StatusChip labelHe={`בשימוש ${refusal.current}`} tone="warn" />{' '}
        <StatusChip labelHe={`נרכשו ${refusal.limit}`} tone="muted" />
      </p>
      <a className="btn btn--primary" href={text.upgradeHref}>
        {text.nextStepHe}
      </a>
    </section>
  );
}
